import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppMenu from "../components/AppMenu";
import { title } from "../config";
import { getServers, deleteServer, dropServerCaches } from "../services/serverAPI";
import { Table } from "reactstrap";
import { BackButton } from "../components/BackButton";
import { useToast } from "../components/ToastContext";
import moment from "moment";
import "../assets/css/Table.css";

export default function Servers() {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [servers, setServers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [droppingId, setDroppingId] = useState(null);
  const [searchText, setSearchText] = useState("");

  const loadServers = async () => {
    try {
      setLoading(true);
      const data = await getServers(showToast);
      setServers(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error loading servers:", err);
      setServers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleManualRefresh = async () => {
    try {
      setRefreshing(true);
      const data = await getServers(showToast);
      setServers(Array.isArray(data) ? data : []);
      showToast("Server metrics reloaded successfully!", "success");
    } catch (err) {
      console.error("Error refreshing servers:", err);
      showToast("Failed to refresh servers", "error");
    } finally {
      setRefreshing(false);
    }
  };

  const handleDropCaches = async (server) => {
    const confirmed = window.confirm(
      `Drop RAM caches on ${server.name}?\n\nThis will execute:\nsync\necho 1 > /proc/sys/vm/drop_caches\necho 2 > /proc/sys/vm/drop_caches\necho 3 > /proc/sys/vm/drop_caches`
    );
    if (!confirmed) return;

    try {
      setDroppingId(server._id);
      showToast(`Clearing RAM caches on ${server.name}...`, "info");
      const res = await dropServerCaches(server._id, showToast);
      if (res?.success) {
        showToast(res.message || "RAM caches dropped successfully!", "success");
        await loadServers();
      }
    } catch (err) {
      console.error("Error dropping caches:", err);
      showToast("Failed to drop caches", "error");
    } finally {
      setDroppingId(null);
    }
  };

  useEffect(() => {
    loadServers();
  }, []);

  const handleDelete = async (id, name) => {
    if (window.confirm(`Are you sure you want to delete server '${name}'?`)) {
      const res = await deleteServer(id, showToast);
      if (res?.result === "success") {
        showToast("Server deleted successfully", "success");
        loadServers();
      }
    }
  };

  const filteredServers = servers.filter((server) => {
    const text = searchText.toLowerCase();
    return (
      (server.name && server.name.toLowerCase().includes(text)) ||
      (server.hostname && server.hostname.toLowerCase().includes(text)) ||
      (server.ip && server.ip.toLowerCase().includes(text)) ||
      (server.description && server.description.toLowerCase().includes(text))
    );
  });

  const formatGB = (bytes) => {
    if (!bytes || isNaN(bytes)) return "-";
    return `${(bytes / 1073741824).toFixed(2)} GB`;
  };

  return (
    <div>
      <div className="px-2 sticky-header">
        <h3 className="m-0 p-2">{title} | Servers</h3>
        <AppMenu />
      </div>

      <div className="p-0 px-2 m-0">
        <div className="site-toolbar px-2 d-flex justify-content-between align-items-center mb-3">
          <div className="site-toolbar-left d-flex align-items-center gap-2">
            <BackButton icon="←" />
            <input
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="Search Server Name / IP"
              type="search"
              className="site-search form-control"
              style={{ width: "260px" }}
            />
            <span className="site-total ms-2">Total: {filteredServers.length}</span>
          </div>

          <div className="d-flex align-items-center gap-2">
            <button
              className="btn btn-outline-primary"
              onClick={handleManualRefresh}
              disabled={refreshing || loading}
              title="Reload latest server metrics from database"
            >
              <i className={`fas fa-sync-alt me-1 ${refreshing ? "fa-spin" : ""}`}></i>
              {refreshing ? "Refreshing..." : "Refresh"}
            </button>
            <button
              className="btn btn-primary"
              onClick={() => navigate("/server/add")}
            >
              <i className="fas fa-plus me-1"></i> Add Server
            </button>
          </div>
        </div>

        <div className="site-table-container" style={{ paddingBottom: "calc(40px + 2rem)" }}>
          <Table className="table table-hover site-table" responsive hover>
            <thead>
              <tr>
                <th>Sr. No.</th>
                <th>Server Name</th>
                <th>Hostname / IP</th>
                <th>RAM Usage</th>
                <th>Disk Storage</th>
                <th>CPU Load</th>
                <th>Linked Sites</th>
                <th>Last Updated</th>
                <th>Status</th>
                <th className="text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="10" className="text-center py-4">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </td>
                </tr>
              ) : filteredServers.length === 0 ? (
                <tr>
                  <td colSpan="10" className="text-center py-4 text-muted">
                    No servers registered. Click "Add Server" to get started.
                  </td>
                </tr>
              ) : (
                filteredServers.map((server, index) => {
                  const ramPercent = server.ramUsagePercent;
                  const isHighRam = ramPercent !== undefined && ramPercent >= 80;
                  const isCriticalRam = ramPercent !== undefined && ramPercent >= 90;

                  const diskPercent = server.diskUsagePercent;
                  const isHighDisk = diskPercent !== undefined && diskPercent >= 80;
                  const isCriticalDisk = diskPercent !== undefined && diskPercent >= 90;

                  return (
                    <tr key={server._id || index}>
                      <td>{index + 1}</td>
                      <td>
                        <strong>{server.name}</strong>
                        {server.description && <div className="text-muted small">{server.description}</div>}
                      </td>
                      <td>
                        <div>{server.hostname || "-"}</div>
                        {server.ip && server.ip !== server.hostname && (
                          <div className="text-muted small">{server.ip}</div>
                        )}
                      </td>
                      <td>
                        {server.totalMem ? (
                          <div>
                            <div className="d-flex justify-content-between small text-muted mb-1" style={{ fontSize: "0.78rem" }}>
                              <span><strong>Used:</strong> {server.usedMem !== undefined ? `${server.usedMem} MB` : `${server.totalMem - (server.freeMem || 0)} MB`}</span>
                              <span><strong>Cache:</strong> {server.cacheMem !== undefined ? `${server.cacheMem} MB` : "-"}</span>
                              <span><strong>Avail:</strong> {server.freeMem || 0} MB</span>
                            </div>
                            <div className="progress" style={{ height: "7px" }}>
                              <div
                                className={`progress-bar ${isCriticalRam ? "bg-danger" : isHighRam ? "bg-warning" : "bg-primary"}`}
                                role="progressbar"
                                title={`Occupied: ${ramPercent}%`}
                                style={{ width: `${ramPercent || 0}%` }}
                              ></div>
                              {server.cacheMem > 0 && (
                                <div
                                  className="progress-bar bg-info bg-opacity-75"
                                  role="progressbar"
                                  title={`Cache: ${Math.round((server.cacheMem / server.totalMem) * 100)}%`}
                                  style={{ width: `${Math.round((server.cacheMem / server.totalMem) * 100)}%` }}
                                ></div>
                              )}
                            </div>
                            <div className="mt-1 d-flex gap-1 flex-wrap" style={{ fontSize: "0.72rem" }}>
                              <span className={`badge ${isCriticalRam ? "bg-danger" : isHighRam ? "bg-warning text-dark" : "bg-primary"}`}>
                                {ramPercent}% Occupied
                              </span>
                              {server.cacheMem > 0 && (
                                <span className="badge bg-secondary">
                                  {Math.round((server.cacheMem / server.totalMem) * 100)}% Cache
                                </span>
                              )}
                              <span className="badge bg-success">
                                {100 - (ramPercent || 0)}% Available
                              </span>
                            </div>
                          </div>
                        ) : (
                          "-"
                        )}
                      </td>
                      <td>
                        {server.size ? (
                          <div>
                            <div className="d-flex justify-content-between small">
                              <span>{formatGB(server.free)} Free</span>
                              <span>{formatGB(server.size)}</span>
                            </div>
                            <div className="progress" style={{ height: "6px" }}>
                              <div
                                className={`progress-bar ${isCriticalDisk ? "bg-danger" : isHighDisk ? "bg-warning" : "bg-info"}`}
                                role="progressbar"
                                style={{ width: `${diskPercent || 0}%` }}
                              ></div>
                            </div>
                            <span className={`badge mt-1 ${isCriticalDisk ? "bg-danger" : isHighDisk ? "bg-warning text-dark" : "bg-secondary"}`}>
                              {diskPercent}% full
                            </span>
                          </div>
                        ) : (
                          "-"
                        )}
                      </td>
                      <td>{server.cpuLoad ? (Array.isArray(server.cpuLoad) ? server.cpuLoad.join(" / ") : server.cpuLoad) : "-"}</td>
                      <td>
                        <span className="badge bg-primary rounded-pill">
                          {server.linkedSitesCount || 0} sites
                        </span>
                      </td>
                      <td>
                        {server.lastUpdated ? moment(server.lastUpdated).format("DD MMM YYYY, hh:mm A") : "-"}
                      </td>
                      <td>
                        {server.active !== 0 && server.active !== false ? (
                          <span className="badge bg-success">Active</span>
                        ) : (
                          <span className="badge bg-secondary">Inactive</span>
                        )}
                      </td>
                      <td className="text-center">
                        <div className="d-flex justify-content-center align-items-center gap-1">
                          <button
                            className="btn btn-sm btn-outline-warning text-dark"
                            onClick={() => handleDropCaches(server)}
                            disabled={droppingId === server._id}
                            title="Drop RAM Caches (echo 1, 2, 3 > /proc/sys/vm/drop_caches)"
                          >
                            <i className={`fas fa-broom ${droppingId === server._id ? "fa-spin" : ""}`}></i>
                          </button>
                          <button
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => navigate(`/server/edit/${server._id}`)}
                            title="Edit Server"
                          >
                            <i className="fas fa-edit"></i>
                          </button>
                          <button
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => handleDelete(server._id, server.name)}
                            title="Delete Server"
                          >
                            <i className="fas fa-trash-alt"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </Table>
        </div>
      </div>
    </div>
  );
}
